import { createContext, useCallback, useContext, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'

export type DiyAnimationBudgetApi = {
  acquire: () => boolean
  release: () => void
}

const DiyAnimationBudgetContext = createContext<DiyAnimationBudgetApi | null>(null)

/**
 * Limits how many {@link AnimatedValue} highlight ripples may run at once (Story 2.1, Epic 2.6, Epic 2.11 product closure).
 * When the budget is exhausted, extra value changes update the **text** without the highlight (no queueing, no backlog).
 * Slots are granted in **child effect / tree order**, not by sorting on `priority`; stagger delays remain per-`priority` in {@link AnimatedValue}.
 * @see Epic Story 2.11: `ProjectGuidePage` wraps the guide with `maxConcurrent={3}`; pair with `AnimatedValue` P1/P2/P3 `transitionDelay`.
 */
export function DiyAnimationBudgetProvider({
  maxConcurrent = 3,
  children,
}: {
  maxConcurrent?: number
  children: ReactNode
}) {
  const activeRef = useRef(0)
  const maxRef = useRef(maxConcurrent)
  maxRef.current = maxConcurrent

  const acquire = useCallback(() => {
    if (activeRef.current >= maxRef.current) {
      return false
    }
    activeRef.current++
    return true
  }, [])

  const release = useCallback(() => {
    activeRef.current = Math.max(0, activeRef.current - 1)
  }, [])

  const value = useMemo(
    () => ({
      acquire,
      release,
    }),
    [acquire, release]
  )

  return (
    <DiyAnimationBudgetContext.Provider value={value}>{children}</DiyAnimationBudgetContext.Provider>
  )
}

export function useDiyAnimationBudget(): DiyAnimationBudgetApi | null {
  return useContext(DiyAnimationBudgetContext)
}
